import Contentstack from 'contentstack';
import { ProductCategoryKeys } from '../common/enums/common';
import { TyRegion } from '../types';
import { safeGet } from '../utils/objectUtils';
import { REGION_MAP, getRegionFromUri } from '../utils/regionUtils';
import {
  unwrapBanner,
  unwrapMarketingGrid,
  unwrapMarketingTiles,
  unwrapMarketingTilesSpacing,
  unwrapRegionPicker,
} from '../utils/unwrapCSComponentModels';
import { KPContentType } from '../types/contentStackRef';

export type ISiteMap = {
  [key: string]: any[];
};

export const SiteMap = {
  SITE_MAP_LINK: 'SITE_MAP_LINK',
  SITE_MAP_DIALOG: 'SITE_MAP_DIALOG',
};

export default class ContentService {
  private _stack: Contentstack.Stack;
  constructor(config: Contentstack.Config) {
    this._stack = Contentstack.Stack(config);
  }

  //get Sitemap
  async getSiteMap(config: any) {
    const queryForSiteMapLink = this._stack.ContentType('site_map_link').Query();
    const queryForSiteMapDialog = this._stack.ContentType('site_map_dialog').Query();
    const responseLink = await queryForSiteMapLink.toJSON().find();
    const responseDialog = await queryForSiteMapDialog.toJSON().find();
    const siteMap: ISiteMap = {};
    Promise.all([responseLink, responseDialog]).then(values => {
      const siteLinkResult: any = [];
      const siteDialogResult: any = [];
      values[0]?.[0].forEach(function (value: any) {
        let isExternal = false;
        value.app = value.app || 'kp2020';
        if (value?.app !== 'kp2020') {
          isExternal = true;
          value.url = `${config.nat.kpApps[value.app].url}` + `${value.url}`;
        }

        siteLinkResult[value.uid] = {
          url: value.url,
          title: value.title,
          app: value.app,
          isExternal,
        };
      });
      values[1]?.[0].forEach(function (value: any) {
        siteDialogResult[value.uid] = { action: value.action };
      });
      siteMap[SiteMap.SITE_MAP_LINK] = siteLinkResult;
      siteMap[SiteMap.SITE_MAP_DIALOG] = siteDialogResult;
    });
    return siteMap;
  }

  async getHeader() {
    const query = this._stack.ContentType('site_header').Query();
    const response = await query
      .where('region.code', REGION_MAP[getRegionFromUri().toUpperCase() as TyRegion])
      .limit(1)
      .includeReference([
        'main_navigation_menu',
        'top_navigation_toolbar',
        'middle_navigation_toolbar',
        'main_navigation_menu.site_navigation_new_menu',
        'main_navigation_menu.navigation_blocks.navigation_link.header_link',
        'top_navigation_toolbar.header_navigation_new_toolbar',
        'top_navigation_toolbar.tool_bar.toolbar_links.header_navigation_new_toolbar', // region list
        'top_navigation_toolbar.header_navigation_new_toolbar.site_navigation_new_menu', // For My account
        'top_navigation_toolbar.tool_bar.toolbar_links.items.select_list.list',
        'middle_navigation_toolbar.header_navigation_new_toolbar',
        'middle_navigation_toolbar.toolbar.toolbar_links.header_navigation_new_toolbar',
        'web_notifications.error_message.messages.alert',
      ])
      .toJSON()
      .find();
    return response;
  }

  async getPrintRxPage() {
    const query = this._stack.ContentType('print_rx').Query();
    const response = await query.toJSON().find();
    const printContent = response[0];

    return {
      printUserInstruciton:
        printContent[0].body.print_user_instruction.children[0].children[0].text,
      logo: printContent[0].header.logo,
    };
  }

  async getAlertBaner(alertType: string) {
    const query = this._stack.ContentType('alert').Query();
    const response = await query.toJSON().find();
    const printContent = response[0];
    const alertErrorContent = printContent.filter((al: any) => al.alert_code == alertType);
    const alertContent = {
      header: safeGet(alertErrorContent[0].content, 'heading'),
      message: safeGet(alertErrorContent[0].content, 'message'),
    };

    return alertContent;
  }

  async getInvoice() {
    const query = this._stack.ContentType('printinvoice').Query();
    const response = await query
      .where('region.code', REGION_MAP[getRegionFromUri().toUpperCase() as TyRegion])
      .toJSON()
      .find();
    const data = response[0][0];
    return {
      featureFlag: data.feature_flags.display_template,
      logo: data.header.logo,
      telephoneDetails: data.header.contact_details,
      footerDesc: data.footer.footer_description,
    };
  }

  async getStoreOperationHours() {
    const query = this._stack.ContentType('store').Query();
    const response = await query.toJSON().find();
    const storeContent = (response?.[0] || []).map((item: any) => {
      return {
        storeID: item.store_id,
        storeHours: item.store_hours,
      };
    });

    return {
      storeContent,
    };
  }

  async getFooter() {
    const query = this._stack.ContentType('page_footer').Query();
    const response = await query
      .where('region.code', REGION_MAP[getRegionFromUri().toUpperCase() as TyRegion])
      .limit(1)
      .includeReference(['navigation_menu', 'bottom_navigation_links.navigation_menu'])
      .toJSON()
      .find();
    return response[0]?.[0];
  }

  async getRegionPicker(contentType: string) {
    const query = this._stack.ContentType(contentType).Query();
    const response = await query.toJSON().find();
    if (!response.length) return null;
    return unwrapRegionPicker(response);
  }

  async getNotification(region: string) {
    const query = this._stack.ContentType('notification').Query();
    //to be changed later according to region
    return await query.where('global_region.code', region).toJSON().limit(1).find();
  }

  async getPage() {
    return;
  }

  async getBrandInfo(content: any, productCategory: ProductCategoryKeys, brandCode?: string) {
    const query = this._stack.ContentType('about_the_brand').Query();
    const queryContentRefs: { [key: string]: string } = {
      plp_banner: 'brand_banner',
      plp_product_tiles_grid: 'plp_product_tiles_grid',
      plp_marketing_grid_content:
        'marketing_grid.select_marketing_content_products.content_row.reference',
      plp_marketing_Grid_Tiles:
        'marketing_grid.select_marketing_content_products.stack_tiles_horizontal_.reference',
    };
    const queryContentsRefsArr = Object.keys(queryContentRefs).map(key => queryContentRefs[key]);
    const SITE_MAP = content?.siteMap;
    const response = await query
      .includeReference(queryContentsRefsArr)
      .where('brand_code', brandCode as string)
      .where('region.code', REGION_MAP[getRegionFromUri().toUpperCase() as TyRegion])
      .where('product_category.category', productCategory)
      .toJSON()
      .find()
      .then(result => {
        return result[0].map((brand: any) => {
          const brandPanel = brand?.about_the_brand_panel;
          const ACTION_UID = safeGet(brandPanel?.call_to_action?.[0]?.page?.[0], 'uid', '');
          const reshapedResult = {
            layout: safeGet(brandPanel, 'layout', 'image'),
            label: safeGet(brandPanel, 'label', 'About the brand'),
            code: safeGet(brand, 'brand_code', 'Brand code'),
            title: safeGet(brandPanel, 'brand_title', 'Brand title'),
            description: safeGet(brandPanel, 'brand_description', 'Brand description'),
            banner: unwrapBanner(brand?.brand_banner, SITE_MAP),
            marketingTiles: unwrapMarketingTiles(brand?.plp_product_tiles_grid, SITE_MAP),
            marketingTilesSpacing: unwrapMarketingTilesSpacing(brand?.plp_product_tiles_grid),
            marketingGrid: unwrapMarketingGrid(brand?.marketing_grid, SITE_MAP),
            brandImage: {
              alt: safeGet(brandPanel, 'brand_image.title', 'Brand title'),
              src: safeGet(
                brandPanel,
                'brand_image.url',
                'https://images.contentstack.io/v3/assets/bltd38de548fe5a8d52/blt4d0b84d1052e437b/62b1dc69482ba357e65de254/banner1.png',
              ),
            },
            carouselData: safeGet(brandPanel, '', []),
            actionLinks: [
              {
                title: safeGet(brandPanel?.call_to_action?.[0], 'title', 'Learn more'),
                url: SITE_MAP?.SITE_MAP_LINK[ACTION_UID]?.url,
              },
            ],
          };
          return reshapedResult;
        });
      })
      .catch(error => {
        console.error(
          'Error: could not retreive about_the_brand content from content stack',
          error,
        );
      });
    return response;
  }

  async getContentPageByQuery({
    slug,
    region,
    refs,
    contentType,
    hasMarketingContent,
    additionalQueryParams = {},
    all,
  }: {
    slug: string;
    region: string;
    refs: string[];
    contentType: string;
    hasMarketingContent?: boolean;
    additionalQueryParams?: any;
    all?: boolean;
  }) {
    const pageRefs = [...refs];
    if (hasMarketingContent !== false) {
      pageRefs.push(
        'marketing_grid.select_marketing_content_products.content_row.reference',
        'marketing_grid.select_marketing_content_products.stack_tiles_horizontal_.reference',
      );
    }
    let query = this._stack
      .ContentType(contentType)
      .Query()
      .where('page_name.page_name', slug)
      .where('region.code', region);

    if (additionalQueryParams) {
      const keys = Object.keys(additionalQueryParams);
      keys.forEach(param => {
        query = query.where(param, additionalQueryParams[param]);
      });
    }
    const response = await query.includeReference(pageRefs).toJSON().find();
    return { response: response, contentType: contentType, all: all ? true : false };
  }

  async getLensWizardContent() {
    const lensWizardOptionsQuery = this._stack
      .ContentType(KPContentType.LensWizardOption)
      .Query()
      .where('region.code', REGION_MAP[getRegionFromUri().toUpperCase() as TyRegion])
      .includeReference(['option.eyeglass_lens_wizard_option_id'])
      .toJSON();

    const lensWizardCollectionsQuery = this._stack
      .ContentType(KPContentType.LensWizardCollection)
      .Query()
      .where('region.code', REGION_MAP[getRegionFromUri().toUpperCase() as TyRegion])
      .includeReference(['collection.eyeglass_lens_wizard_collection_id'])
      .toJSON();

    const lensWizardMessagesQuery = this._stack
      .ContentType(KPContentType.LensWizardMessage)
      .Query()
      .where('region.code', REGION_MAP[getRegionFromUri().toUpperCase() as TyRegion])
      .includeReference(['message.eyeglass_lens_wizard_message_id'])
      .toJSON();

    const [lensWizardOptionsResponse, lensWizardCollectionsResponse, lensWizardMessagesResponse] =
      await Promise.all([
        lensWizardOptionsQuery.find(),
        lensWizardCollectionsQuery.find(),
        lensWizardMessagesQuery.find(),
      ]);

    return {
      lensWizardOptions: lensWizardOptionsResponse,
      lensWizardCollections: lensWizardCollectionsResponse,
      lensWizardMessages: lensWizardMessagesResponse,
    };
  }
}
